<script lang="ts" setup>
/**
 * @link https://idsk.gov.sk/komponenty/formularove-prvky
 * @link https://idsk.gov.sk/komponenty/chyby-a-validacia
 */
import { computed, TextareaHTMLAttributes, onMounted, ref } from 'vue'

export interface IDSKTextareaProps extends TextareaHTMLAttributes {
  label?: string
  hint?: string
  hintId?: string
  /** Error message text, also serves as a flag whether the input is valid. */
  errorMessage?: string
  errorMessageId?: string
  readOnly?: boolean
  modelValue: string
  placeholder?: string
  dataTestId?: string
}

const props = withDefaults(defineProps<IDSKTextareaProps>(), {
  label: undefined,
  hint: undefined,
  hintId: undefined,
  errorMessage: undefined,
  errorMessageId: undefined,
  placeholder: undefined,
  dataTestId: '',
})

const emit = defineEmits(['update:modelValue'])

const isValid = computed<boolean>(() => !props.errorMessage)
const areaRef = ref<HTMLTextAreaElement>()

const updateHeight = () => {
  const el = areaRef.value
  if (el?.style) {
    el.style.height = ''
    el.style.height = el.scrollHeight + 'px'
    el.scrollTop = el.selectionEnd < el.value?.length ? el.scrollTop : el.scrollHeight
  }
}

onMounted(() => {
  updateHeight()
})

const handleInput = (e: any) => {
  emit('update:modelValue', e.target.value)
  updateHeight()
}

const formatOnBlur = (e: any) => {
  const formattedVal = e.target.value.replaceAll(/\s+/g, '').trimStart().trimEnd()
  emit('update:modelValue', formattedVal)
}
</script>

<template>
  <div
    :class="[
      'govuk-form-group',
      {
        ['govuk-form-group--error']: errorMessage,
      },
    ]"
  >
    <IDSKFieldCaption
      :label="label"
      :for="$attrs.id"
      :hint="hint"
      :hint-id="hintId"
      :error-message="errorMessage"
      :error-message-id="errorMessageId"
    />
    <textarea
      :ref="(el) => (areaRef = el)"
      v-bind="$attrs"
      :value="modelValue"
      :class="[
        'govuk-textarea',
        {
          'govuk-textarea--error': !isValid,
        },
      ]"
      :readonly="readOnly"
      :disabled="disabled"
      :placeholder="placeholder"
      :name="name"
      :aria-describedby="[hintId, errorMessageId].filter(Boolean).join(' ') || undefined"
      :data-test-id="
        props.id ? 'textarea' + '-' + props.id : 'textarea' + props.dataTestId
      "
      @input="handleInput"
    />
  </div>
</template>

<style scoped lang="scss">
textarea {
  min-height: 85px;
  max-height: 250px;

  transition: all 0.195s ease-in;
  padding-left: 10px;

  &:focus {
    margin-right: 3px;
    z-index: 3;
    border: 2px solid govuk-colour('black');
    box-shadow: inset 0 0 0 3px;
  }

  &:hover {
    margin-right: 0px;
    box-shadow: inset 0 0 0 3px;
  }
}
</style>
